// A menu product's picture (F-FNB-01) as Sell's tiles and the menu's rows show it: the uploaded image, cropped to
// fill its frame, or — until the venue uploads one — a tile tinted in one of the categorical colours with the
// product's initial, stable per product so the same dish always wears the same tint. Never a stock or fake
// picture. Decoration only: the product's name is always written beside it, so the art is hidden from
// assistive technology.
import type { UploadedImage } from '@clavian/api-client';
import { cx } from '@clavian/ui-web';
import { useState, type ReactNode } from 'react';

import './product-art.css';

/** A tint, 1–8 (`--clavian-category-N`), from the product's id: the same product always gets the same one. */
export function productTone(id: string): number {
  let hash = 0;
  for (const char of id) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return (hash % 8) + 1;
}

/** The product's initial, as the placeholder shows it: the first letter or digit of its name. */
export function productInitial(name: string): string {
  const match = /[\p{L}\p{N}]/u.exec(name);
  return (match?.[0] ?? '·').toLocaleUpperCase();
}

export function ProductArt({
  id,
  name,
  image,
  sizes = '12rem',
  className,
}: {
  readonly id: string;
  readonly name: string;
  readonly image: UploadedImage | null | undefined;
  /** The `sizes` for the picture's `srcset`: how wide the frame is drawn. */
  readonly sizes?: string;
  readonly className?: string;
}): ReactNode {
  // The worker makes the smaller sizes seconds after an upload; until then (or if one fails) the original — and if
  // the original will not load either, the tinted placeholder, never the browser's broken-image glyph.
  const [failures, setFailures] = useState(0);
  const variantsFailed = failures > 0;
  // With variants the first failure drops them and the original is tried; without, the original was all there was.
  const giveUpAt = image !== null && image !== undefined && image.variants.length > 0 ? 2 : 1;
  const shown = image === null || image === undefined || failures >= giveUpAt ? null : image;
  const srcSet = shown === null || variantsFailed ? '' : shown.variants.map((variant) => `${variant.url} ${variant.width}w`).join(', ');
  return (
    <span className={cx('console-product-art', className)} data-tone={productTone(id)} aria-hidden="true">
      {shown === null ? (
        <span className="console-product-art__initial">{productInitial(name)}</span>
      ) : (
        <img
          className="console-product-art__image"
          src={shown.url}
          alt=""
          loading="lazy"
          decoding="async"
          {...(srcSet === '' ? {} : { srcSet, sizes })}
          onError={() => setFailures((count) => count + 1)}
        />
      )}
    </span>
  );
}

/**
 * A game's or an app's cover as the library shows it: the image, cropped to fill its frame — or, with none, or one that
 * will not load, the same tinted plate and initial as a dish (ADR-0043), never the browser's broken-image glyph.
 * Decoration only: the title is always written beside it.
 */
export function CoverArt({ id, name, src, className }: { readonly id: string; readonly name: string; readonly src: string | null; readonly className?: string }): ReactNode {
  // A new address is a new picture to try.
  const [failed, setFailed] = useState<string | null>(null);
  const shown = src !== null && failed !== src ? src : null;
  return (
    <span className={cx('console-product-art', className)} data-tone={productTone(id)} aria-hidden="true">
      {shown === null ? (
        <span className="console-product-art__initial">{productInitial(name)}</span>
      ) : (
        // A cover's 2:3 shape as its intrinsic size, so nothing moves while it loads (the frame sets the drawn size).
        <img className="console-product-art__image" src={shown} alt="" width={132} height={198} loading="lazy" decoding="async" onError={() => setFailed(shown)} />
      )}
    </span>
  );
}
